/**
 * Field Guide SEO helpers — hreflang + JSON-LD.  Mirrors `features/wiki/seo.ts`
 * but with Article/HowTo schema appropriate for documentation pages.
 */

import { useEffect } from 'react';
import {
  buildGuideHreflangAlternates,
  buildGuideUrl,
  type ArticleSlug,
  type LangCode,
  type SectionSlug,
} from './architecture';

const PROD_ORIGIN = 'https://lodestar.place';

function resolveOrigin(): string {
  if (typeof window === 'undefined') return PROD_ORIGIN;
  return window.location.origin || PROD_ORIGIN;
}

/* ────────────────────────────────────────────────────────────────── */
/*  Hreflang                                                           */
/* ────────────────────────────────────────────────────────────────── */

export interface GuideHreflangOpts {
  section?: SectionSlug;
  article?: ArticleSlug;
}

export function useGuideHreflang(opts: GuideHreflangOpts): void {
  useEffect(() => {
    const origin = resolveOrigin();
    const alternates = buildGuideHreflangAlternates({ ...opts, origin });

    const owned: HTMLLinkElement[] = [];
    for (const { hreflang, href } of alternates) {
      const selector = `link[rel="alternate"][hreflang="${hreflang}"][data-rm-guide="1"]`;
      let el = document.head.querySelector<HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        el.setAttribute('data-rm-guide', '1');
        document.head.appendChild(el);
        owned.push(el);
      }
      el.setAttribute('href', href);
    }

    return () => {
      for (const el of owned) el.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.section, opts.article]);
}

/* ────────────────────────────────────────────────────────────────── */
/*  JSON-LD                                                            */
/* ────────────────────────────────────────────────────────────────── */

interface BreadcrumbItem {
  name: string;
  url: string;
}

function buildBreadcrumbJsonLd(items: BreadcrumbItem[]): unknown {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export interface GuideArticleJsonLdOpts {
  lang: LangCode;
  section: SectionSlug;
  article: ArticleSlug;
  guideLabel: string;
  sectionLabel: string;
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  author: string;
  faqs?: Array<{ question: string; answer: string }>;
}

export function buildGuideArticleJsonLd(opts: GuideArticleJsonLdOpts): string {
  const origin = resolveOrigin();
  const articleUrl = buildGuideUrl({
    lang: opts.lang,
    section: opts.section,
    article: opts.article,
    origin,
  });
  const sectionUrl = buildGuideUrl({
    lang: opts.lang,
    section: opts.section,
    origin,
  });
  const guideUrl = buildGuideUrl({ lang: opts.lang, origin });

  const graph: unknown[] = [
    buildBreadcrumbJsonLd([
      { name: opts.guideLabel, url: guideUrl },
      { name: opts.sectionLabel, url: sectionUrl },
      { name: opts.headline, url: articleUrl },
    ]),
    {
      '@context': 'https://schema.org',
      '@type': 'TechArticle',
      headline: opts.headline,
      description: opts.description,
      url: articleUrl,
      datePublished: opts.datePublished,
      dateModified: opts.dateModified,
      author: { '@type': 'Person', name: opts.author },
      inLanguage: opts.lang,
      isPartOf: { '@type': 'WebSite', url: origin, name: 'lodestar.place' },
    },
  ];

  if (opts.faqs && opts.faqs.length > 0) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: opts.faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}
